import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { thumbUrl } from "@/lib/utils-app";
import { Input } from "@/components/ui/input";
import { DogIcon as DogIcon, XIcon as X, MagnifyingGlassIcon as MagnifyingGlass, CheckIcon as Check } from "@phosphor-icons/react";

/**
 * Search-as-you-type dog picker backed by `/api/search`.
 *
 * Replaces a `<Select>` whose only choices were the dogs the current
 * user could see at page-load time. By searching the full registry
 * (filtered to the desired sex via `sexFilter`) the user can pick
 * sires/dams that *exist anywhere in the system* — eliminating the
 * accidental duplicate dog records that result when an owner can't find
 * the parent and creates a new entry.
 *
 * Props:
 *   value         — current selected dog id (string | "")
 *   onChange      — (id, fullDogObject|null) => void
 *   sexFilter     — "male" | "female" | undefined (no filter)
 *   placeholder   — input placeholder text
 *   excludeIds    — dog ids to hide (e.g. the dog being edited can't be its own parent)
 *   testId        — base data-testid
 */
export default function DogPicker({
  value = "",
  onChange,
  sexFilter,
  placeholder = "Search by name or registration #",
  excludeIds = [],
  testId = "dog-picker",
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [selected, setSelected] = useState(null); // hydrated dog summary for display
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  // When `value` changes externally (e.g. parent loads existing dog), hydrate
  // the summary so the chip reflects the right name + breed.
  useEffect(() => {
    let cancelled = false;
    if (!value) {
      setSelected(null);
      return;
    }
    if (selected?.id === value) return;
    api.get(`/dogs/${value}`).then((r) => {
      if (cancelled) return;
      setSelected({
        id: r.data.id,
        name: r.data.name,
        breed: r.data.breed,
        registration_number: r.data.registration_number,
        sex: r.data.sex,
        photo: (r.data.photos || [""])[0] || "",
      });
    }).catch(() => setSelected({ id: value, name: "Unknown dog", breed: "", registration_number: "", sex: "", photo: "" }));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounced search.
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: q.trim(), limit: 10 } });
        setResults(data.dogs || []);
        setActive(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(timerRef.current);
  }, [q]);

  // Close popover on outside-click.
  useEffect(() => {
    const onDoc = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    // Defensive: some legacy / CSV-imported / migrated dogs carry
    // `sex` as "Female" or "FEMALE" instead of the schema-enforced
    // lowercase form. The Literal validator runs only on Pydantic
    // ingress, not on direct collection writes (admin tools, bulk
    // reassign, csv backfill), so we normalise on the read side.
    const wantSex = (sexFilter || "").toLowerCase();
    return results.filter((d) => {
      if (excludeIds.includes(d.id)) return false;
      const dogSex = (d.sex || "").toLowerCase();
      if (wantSex && dogSex && dogSex !== wantSex) return false;
      return true;
    });
  }, [results, excludeIds, sexFilter]);

  const choose = (dog) => {
    setSelected(dog);
    onChange?.(dog.id, dog);
    setQ("");
    setResults([]);
    setOpen(false);
    inputRef.current?.blur();
  };

  const clear = () => {
    setSelected(null);
    onChange?.("", null);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      const target = filtered[active];
      if (target) {
        e.preventDefault();
        choose(target);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  if (selected) {
    return (
      <div
        className="flex items-center gap-3 rounded-md border border-[#E5E5E5] bg-white px-3 py-2"
        data-testid={`${testId}-selected`}
      >
        <div className="w-9 h-9 rounded-md bg-[#FAFAFA] overflow-hidden flex-shrink-0">
          {selected.photo ? (
            <img src={thumbUrl(selected.photo)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <DogIcon size={16} weight="duotone" className="text-[#6B6B6B]" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-ink truncate">
            {selected.name}
            {selected.registration_number && (
              <span className="ml-2 text-[10px] font-mono font-normal text-[#6B6B6B]">
                {selected.registration_number}
              </span>
            )}
          </p>
          <p className="text-xs text-[#6B6B6B] truncate">{selected.breed}</p>
        </div>
        <button
          type="button"
          onClick={clear}
          aria-label="Clear selection"
          data-testid={`${testId}-clear`}
          className="p-1.5 rounded-full text-[#6B6B6B] hover:text-crimson hover:bg-[#FAFAFA] transition-colors"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      <div className="relative">
        <MagnifyingGlass
          size={14}
          weight="bold"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B] pointer-events-none"
        />
        <Input
          ref={inputRef}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          className="pl-8 pr-3"
          data-testid={`${testId}-input`}
        />
      </div>
      {open && q.trim().length >= 2 && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 bg-white border border-[#E5E5E5] rounded-lg shadow-lg max-h-64 overflow-y-auto"
          data-testid={`${testId}-results`}
        >
          {loading && <div className="px-3 py-3 text-xs text-[#6B6B6B]">Searching…</div>}
          {!loading && filtered.length === 0 && (
            <div className="px-3 py-4 text-xs text-[#6B6B6B] text-center">
              No matches{sexFilter ? ` for ${sexFilter} dogs` : ""}.<br />
              <span className="text-[10px]">If the dog isn't in the registry yet, leave this blank — it can be added later via Pedigree backfill.</span>
            </div>
          )}
          {!loading && filtered.map((d, i) => (
            <button
              key={d.id}
              type="button"
              onClick={() => choose(d)}
              data-testid={`${testId}-option-${d.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                i === active ? "bg-[#FAFAFA]" : "hover:bg-[#FAFAFA]"
              }`}
            >
              <div className="w-7 h-7 rounded-md bg-[#FAFAFA] overflow-hidden flex-shrink-0">
                {d.photo ? (
                  <img src={thumbUrl(d.photo)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <DogIcon size={12} weight="duotone" className="text-[#6B6B6B]" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink truncate">
                  {d.name}
                  {d.registration_number && (
                    <span className="ml-1.5 text-[10px] font-mono text-[#6B6B6B]">
                      {d.registration_number}
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-[#6B6B6B] truncate">{d.breed}</p>
              </div>
              {i === active && <Check size={12} weight="bold" className="text-gold" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
